import { useState, useEffect, useMemo, useRef } from "react";
import { useAuth0 } from '@auth0/auth0-react';

// react-router components
import { Routes, Route, BrowserRouter } from "react-router-dom";
import theme from "assets/theme";
import { useSoftUIController, setDeviceLayout, setSelectedOrg, setUserPermissions, setHasPassword, setAppearance } from "hooks/context";

// components
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress } from "@mui/material";
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import MapView from "pages/views/MapView";

// mapbox and routes
import mapboxgl from '!mapbox-gl';
import routes from "page_routes_copy";
import userAPI from "services/api/user";
import { useQuery } from '@tanstack/react-query';

import PageLayout from "layouts/PageLayout";
import BaseNavbar from "layouts/BaseNavbar";

import { setMapIcons } from "utils/map";
import moment from "moment-timezone";
import PermissionGuard from "components/Routes/PermissionGuard";
import { NotApprovedUserPage } from "pages/unauthorised";
import { ForbiddenPage } from "pages/unauthorised";
import { RedirectingPage } from "pages/unauthorised";
import { LoggedOutPage } from "pages/unauthorised";

const getActiveMode = (userMode) => {
    if (userMode !== "system") return userMode;
    if (!window.matchMedia) return "light";
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? "dark" : "light";
};

const getMapStyle = (activeVisualMode) => {
    switch (activeVisualMode) {
        case 'dark':
            return "mapbox://styles/mapbox/dark-v11";
        case 'light':
            return "mapbox://styles/harrymontano/cmn5irh6q001o01snbw5yembt?optimize=true";
        case 'dark-satellite':
        case 'light-satellite':
            return "mapbox://styles/mapbox/satellite-streets-v12";
        default:
            return "mapbox://styles/mapbox/dark-v11";
    }
}

export default function NewApp() {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const autoCenterTimeout = useRef(null);
    const interactionTimeout = useRef(null);
    const isUserInteracting = useRef(false);

    const [controller, dispatch] = useSoftUIController();
    const { deviceLayout, selectedOrg } = controller;
    const [isLoaded, setIsLoaded] = useState(false);

    const { loginWithRedirect, isAuthenticated, user, error, isLoading: isAuthLoading, logout } = useAuth0();


    // Handle Auth0 callback errors
    useEffect(() => {
        if (error) {
            // Check for the specific "connection is not enabled" error
            // The SDK maps "error_description" to error.message
            logout(
                { logoutParams: { returnTo: window.location.origin } }
            );


        }
    }, [error]);


    userAPI.loadToken(useAuth0());
    const { data: selected_user, isLoading: isUserLoading } = useQuery({
        queryKey: ['getUser', user?.email],
        enabled: !!user?.email,
        queryFn: async () => {
            return await userAPI.getUser();
        }
    });

    const userPermissions = useMemo(() => {
        var perms = selected_user?.organisations.find(o => o.organisation_id === selectedOrg)?.permissions || [];
        return perms;
    }, [selected_user?.organisations, selectedOrg]);

    const activeVisualMode = useMemo(() => {
        return getActiveMode(selected_user?.user?.mode);
    }, [selected_user?.user?.mode]);

    useEffect(() => {
        if (!map.current && mapContainer.current && selected_user && userPermissions.length) {

            map.current = new mapboxgl.Map({
                container: mapContainer.current,
                style: getMapStyle(activeVisualMode),
                projection: 'globe',
                center: Object.values(selected_user?.organisations.find(o => o.organisation_id === selectedOrg)?.location || { lat: 0, lng: 0 }).reverse(),
                interactive: true,
                antialias: true
            });

            let geolocate;

            // 1. Check permissions safely using standard Array.includes()
            if (deviceLayout === "mobile" && userPermissions.includes('misc:user-location')) {
                geolocate = new mapboxgl.GeolocateControl({
                    positionOptions: { enableHighAccuracy: true },
                    trackUserLocation: true,
                    showUserHeading: true,
                    showButton: true
                });

                map.current.addControl(geolocate);

                // 2. Use official public events instead of modifying private methods
                geolocate.on('geolocate', (position) => {
                    if (!isUserInteracting.current) {
                        map.current.flyTo({
                            center: [position.coords.longitude, position.coords.latitude],
                            zoom: 14,
                            essential: true
                        });
                    }
                });
            }

            map.current.on('load', () => {
                map.current.on('movestart', (event) => {
                    if (event.originalEvent) {
                        isUserInteracting.current = true;
                        if (autoCenterTimeout.current) {
                            clearTimeout(autoCenterTimeout.current);
                            autoCenterTimeout.current = null;
                        }
                        clearTimeout(interactionTimeout.current);
                    }
                });

                map.current.on('moveend', (event) => {
                    if (event.originalEvent) {
                        clearTimeout(interactionTimeout.current);
                        interactionTimeout.current = setTimeout(() => {
                            isUserInteracting.current = false;
                            try {
                                if (geolocate) {
                                    geolocate.trigger(); // Safely prompt public trigger
                                }
                            }
                            catch (e) { }
                        }, 7500);
                    }
                });

                if (map.current.interactive && geolocate) {
                    autoCenterTimeout.current = setTimeout(() => {
                        if (!isUserInteracting.current) {
                            console.log("7.5s idle reached: Triggering geolocation.");
                            try {
                                if (geolocate) {
                                    geolocate.trigger(); // Safely prompt public trigger
                                }
                            }
                            catch (e) { }
                        }
                    }, 7500);
                }

                map.current.originalCenter = map.current.getCenter();
                map.current.interactiveCenter = map.current.getCenter();

                if (activeVisualMode.includes("dark")) {
                    map.current.setFog({
                        color: 'rgba(255,255,255,0.06)',
                        'high-color': 'rgba(170,190,255,0.25)',
                        'space-color': '#05070f',
                        'horizon-blend': 0.18,
                        'star-intensity': 0.25
                    });
                }
                else {
                    map.current.setFog({
                        'color': '#ffffff',
                        'high-color': '#444444',
                        'space-color': '#242424',
                        'horizon-blend': 0.02,
                        'star-intensity': 0.5
                    });
                }
                setIsLoaded(true); // Fixed: Set strict boolean true instead of number 1
            });
        }

        return () => {
            clearTimeout(autoCenterTimeout.current);
            clearTimeout(interactionTimeout.current);
        };
    }, [selected_user, userPermissions, deviceLayout]);

    useEffect(() => {
        if (map.current && isLoaded) {
            const newStyle = getMapStyle(activeVisualMode);
            map.current.setStyle(newStyle);
            map.current.once('style.load', () => {
                if (activeVisualMode.includes('dark')) {
                    map.current.setFog({
                        color: 'rgba(255,255,255,0.06)',
                        'high-color': 'rgba(170,190,255,0.25)',
                        'space-color': '#05070f',
                        'horizon-blend': 0.18,
                        'star-intensity': 0.25
                    });
                }
                else {
                    map.current.setFog({
                        'color': '#ffffff',
                        'high-color': '#444444',
                        'space-color': '#242424',
                        'horizon-blend': 0.02,
                        'star-intensity': 0.5
                    });
                }
                setMapIcons(map, false);
            });
        }
    }, [activeVisualMode]);

    useEffect(() => {
        function handleDeviceLayout() {
            setDeviceLayout(dispatch, window.innerWidth < 1200 ? "mobile" : "desktop");
        }
        window.addEventListener("resize", handleDeviceLayout);
        handleDeviceLayout();
        return () => window.removeEventListener("resize", handleDeviceLayout);
    }, [dispatch]);

    useEffect(() => {
        if (!selected_user || selected_user.user.authorised === false) return;

        let selected_organisation;

        if (localStorage.getItem("selectedOrg")) {
            selected_organisation = selected_user.organisations.find(row => row.organisation_id == localStorage.getItem("selectedOrg"));

            if (!selected_organisation) {
                selected_organisation = selected_user.organisations[0];
            }
        }
        else {
            selected_organisation = selected_user.organisations[0];
        }

        setHasPassword(dispatch, typeof selected_user.user.oidc == 'undefined');
        setSelectedOrg(dispatch, selected_organisation.organisation_id);
        moment.tz.setDefault(selected_organisation.timezone);

        localStorage.setItem("selectedOrg", selected_organisation.organisation_id);

    }, [dispatch, selected_user]);

    // Sync Permissions directly into the shared controller state layer
    useEffect(() => {
        if (selected_user && userPermissions.length > 0) {
            setUserPermissions(dispatch, userPermissions);
        }
    }, [dispatch, selected_user, selectedOrg, userPermissions]);

    useEffect(() => {
        if (selected_user && activeVisualMode) {
            const appearance = 'light_theme' //`${activeVisualMode}_theme`;
            setAppearance(dispatch, appearance);
        }
    }, [dispatch, selected_user]);

    const processedRoutesConfig = useMemo(() => {
        if (!selected_user || !selectedOrg) return routes;

        return routes.map((route) => {
            if (route?.key && route?.key.includes("overview")) {
                const { collapse, ...parentOnly } = route;
                return {
                    ...parentOnly,
                    component: MapView,
                    route: "/overview",
                    in_menu: true
                };
            }
            return route;
        });
    }, [routes, selected_user, selectedOrg]);

    const allRoutes = useMemo(() => {
        if (!selected_user || !selectedOrg) return [];

        return processedRoutesConfig.flatMap(route => {
            const { collapse, ...parent } = route;
            if (!collapse) return parent;
            return [
                parent,
                ...collapse.map(child => ({ ...child, parentKey: parent.key })),
            ];
        });
    }, [processedRoutesConfig, selected_user, selectedOrg]);

    const renderRoutes = useMemo(() => {
        if (!selected_user || !selectedOrg) return [];

        return allRoutes.map(function (route, key) {
            const Component = route.component;

            if (!Component) {
                console.error(`Route at index ${key} has an undefined component!`, route);
                return null;
            }

            return (
                <Route
                    exact
                    path={route.route}
                    key={key}
                    element={
                        /* Pipe permissions down cleanly inside your current wrapper */
                        <PermissionGuard user={selected_user} selectedOrg={selectedOrg} userPermissions={userPermissions}>
                            <Component map={map} user={selected_user} />
                        </PermissionGuard>
                    }
                />
            );
        });
    }, [processedRoutesConfig, selected_user, allRoutes, selectedOrg, userPermissions]);

    if (isAuthLoading || isUserLoading) {
        return (
            <ThemeProvider theme={theme}>
                <Box sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                    background: linearGradient(gradients.dark.main, gradients.dark.state),
                    height: '100%', width: '100%', position: 'fixed', top: '0px', left: '0px', 'z-index': 0, overflow: 'hidden'
                })}>
                    <SoftBox display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight="100%">
                        <CircularProgress color="light" />
                        <SoftTypography variant="button" fontWeight="medium" mt={2} color="light">Loading...</SoftTypography>
                    </SoftBox>
                </Box>
            </ThemeProvider>
        )
    }

    // If there is an error, stop execution so it doesn't loop back to loginWithRedirect
    if (error) {
        return <div>An error occurred: {error.message}</div>;
    }

    const customDomainMap = {
        'mbta.nucleus.mercuryinnovation.com.au': {
            'connection_name': 'MBTA-OpenID'
        },
        'yjm.nucleus.mercuryinnovation.com.au': {
            'connection_name': 'yjm-nucleus'
        },
        'yjm.uat.nucleus.mercuryinnovation.com.au': {
            'connection_name': 'yjm-nucleus'
        },
        'testing.nucleus.mercuryinnovation.com.au': {
            'connection_name': 'OpenID-Connect'
        }
    };

    const hostname = window.location.hostname;

    const logoutRequested = sessionStorage.getItem('logoutRequested') === 'true';

    if(logoutRequested){
        sessionStorage.removeItem('logoutRequested');
    }

    if (!isAuthenticated) {

        let loginOptions = {
            appState: { returnTo: window.location.pathname }
        };

        // 2. Priority 1: Check if the full domain exists in the hash table
        if (logoutRequested) {

            if (customDomainMap[hostname]) {
                //go to redirect page
                if (customDomainMap[hostname].redirect_url) {
                    return <RedirectingPage targetUrl={customDomainMap[hostname].redirect_url} />;
                }
                else {
                    return <LoggedOutPage />
                }

            }
            else {
                loginWithRedirect(loginOptions);
            }
        }
        else {

            if (customDomainMap[hostname]) {
                loginOptions.authorizationParams = {
                    connection: customDomainMap[hostname].connection_name
                };
            }

            loginWithRedirect(loginOptions);
        }

        // 4. Fire the redirect

        return null;
    }
    else {

        if (typeof selected_user == 'undefined' || selected_user?.user.authorised === false) { // if a user is allowed in on the auth0 side but not in the mercury side
            if (customDomainMap[hostname]) {
                // not an approved user - show approve talk to system administrator
                return <NotApprovedUserPage />;
            }
            else {
                //forbidden page
                return <ForbiddenPage />;
            }
        }
    }

    return (
        <BrowserRouter>
            <ThemeProvider theme={theme}>
                <Box component="div" ref={mapContainer}
                    sx={({ palette: { gradients }, functions: { linearGradient } }) => ({
                        background: linearGradient(gradients.dark.main, gradients.dark.state),
                        height: '100%', width: '100%', position: 'fixed', top: '0px', left: '0px', 'z-index': 0, overflow: 'hidden'
                    })}
                />
                <PageLayout>
                    <BaseNavbar
                        map={map}
                        routes={processedRoutesConfig}
                        user={selected_user}
                        appearance={'light_theme'}//`${activeVisualMode}_theme`}
                        menu={true}
                    />
                    <Routes>
                        {isLoaded && renderRoutes}
                    </Routes>
                </PageLayout>
            </ThemeProvider>
        </BrowserRouter>
    );
}